import React, { useState } from 'react';
import { Empleado, BalanceEmpleado } from '../tipos/vacaciones';
import * as balanceService from '../services/balanceService';
import './FormularioBalance.css';

interface FormularioBalanceProps {
  empleado: Empleado;
  balance: BalanceEmpleado | undefined;
  anio: number;
  onGuardar: () => void;
  onCancelar: () => void;
}

const FormularioBalance: React.FC<FormularioBalanceProps> = ({
  empleado,
  balance,
  anio,
  onGuardar,
  onCancelar,
}) => {
  const diasUsados = balance?.diasUsados || 0;
  const [diasTotales, setDiasTotales] = useState<number>(balance?.diasTotales || 0);
  const [guardando, setGuardando] = useState(false);
  const [errorServidor, setErrorServidor] = useState<string | null>(null);

  const diasRestantesCalculados = diasTotales - diasUsados;
  const esMenorQueUsados = diasTotales < diasUsados;
  const esValido = diasTotales >= 1 && diasTotales <= 365 && !esMenorQueUsados;

  const manejarEnvio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!esValido) return;

    setGuardando(true);
    setErrorServidor(null);

    try {
      await balanceService.updateDiasTotales(empleado.id, anio, diasTotales);
      onGuardar();
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : 'Error al actualizar el balance';
      setErrorServidor(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  const manejarClickOverlay = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !guardando) {
      onCancelar();
    }
  };

  return (
    <div className="modal-overlay" onClick={manejarClickOverlay}>
      <div className="modal-formulario modal-balance">
        <div className="modal-formulario-header">
          <h3>
            <i className="fas fa-cog"></i>
            Configurar Dias de Vacaciones
          </h3>
        </div>

        <form className="modal-formulario-body" onSubmit={manejarEnvio}>
          <div className="campo-formulario">
            <label>Empleado</label>
            <div className="campo-readonly">{empleado.nombre}</div>
          </div>

          <div className="campo-formulario">
            <label>Anio</label>
            <div className="campo-readonly">{anio}</div>
          </div>

          <div className="campo-formulario">
            <label htmlFor="bal-dias-totales">Dias totales *</label>
            <input
              id="bal-dias-totales"
              type="number"
              min={1}
              max={365}
              value={diasTotales}
              onChange={(e) => setDiasTotales(Number(e.target.value))}
              disabled={guardando}
            />
          </div>

          <div className="balance-resumen-config">
            <div className="balance-resumen-item">
              <span className="balance-resumen-label">Dias usados:</span>
              <span className="balance-resumen-valor usado">{diasUsados}</span>
            </div>
            <div className="balance-resumen-item">
              <span className="balance-resumen-label">Dias restantes calculados:</span>
              <span className={`balance-resumen-valor ${diasRestantesCalculados > 5 ? 'restante-ok' : diasRestantesCalculados > 0 ? 'restante-bajo' : 'restante-cero'}`}>
                {diasRestantesCalculados}
              </span>
            </div>
          </div>

          {esMenorQueUsados && (
            <div className="error-balance-config">
              <i className="fas fa-exclamation-triangle"></i>
              No puede ser menor que los dias ya usados ({diasUsados})
            </div>
          )}

          {errorServidor && (
            <div className="error-servidor">
              <i className="fas fa-exclamation-circle"></i>
              {errorServidor}
            </div>
          )}

          <div className="modal-formulario-footer">
            <button
              type="button"
              className="btn-modal btn-cancelar"
              onClick={onCancelar}
              disabled={guardando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-modal btn-guardar"
              disabled={guardando || !esValido}
            >
              {guardando ? (
                <>
                  <span className="btn-spinner"></span>
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioBalance;
